import { useEffect, useMemo, useReducer, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Breadcrumbs, Button, Divider, IconButton, Link, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AppsContainer from '@tenant/core/AppsContainer';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import DeleteDialog from '@tenant/core/App/Dialog/DeleteDialog';
import useModal from '@tenant/utility/hooks/useModal';
import useGoBack from '@tenant/utility/hooks/useGoBack';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import { ROUTE_STRING } from 'constants/RouteString';
import queryKeys from 'redux/slices/pipelines/constants/queryKeys';
import useGetDetailPipeline from 'redux/slices/pipelines/hooks/useGetDetailPipeline';
import useGetPipelineBuildSource from 'redux/slices/pipelines/hooks/useGetPipelineBuildSource';
import useGetListPipelines from 'redux/slices/pipelines/hooks/useGetListPipelines';
import useGetListPipelineRuns from 'redux/slices/pipelines/hooks/useGetListPipelineRuns';
import useDeletePipeline from 'redux/slices/pipelines/hooks/useDeletePipeline';
import useGetDetailAppInstance from 'redux/slices/appInstance/hooks/useGetDetailAppInstance';
import { resolvePipelineAppInstanceId } from 'redux/slices/pipelines/helpers/resolvePipelineAppInstanceId';
import { buildPipelineListUrl } from 'redux/slices/pipelines/helpers/pipelineListUrlState';
import { buildContainerNameById } from 'redux/slices/pipelines/helpers/buildContainerNameById';
import { formatRunDuration, formatDurationMs } from 'redux/slices/pipelines/helpers/formatRunDuration';
import { resolveRunBranchDisplay } from 'redux/slices/pipelines/helpers/resolveRunBranchDisplay';
import { PipelineRunStatus, isTerminalPipelineRunStatus } from 'redux/slices/pipelines/constants/pipelineRunStatus';
import PipelineRunStatusChip from './components/Common/PipelineRunStatusChip';
import PipelineRunStatusView from './components/Common/PipelineRunStatusView';
import RunConsole from './components/Detail/RunConsole';
import RunErrorText from './components/Detail/RunErrorText';
import CancelledRunNotice from './components/Detail/CancelledRunNotice';
import SourceConfigPanel from './components/Detail/SourceConfigPanel';
import PipelineRunAction from './components/Detail/PipelineRunAction';
import PipelineEditDialog from './components/List/PipelineEditDialog';

// deploy-pipelines-detail-console — the "run console" redesign of the pipeline detail page
// (AC-1..AC-25). Re-composes the old flat five-box wrapping flow into two regions: a
// config panel (source coordinates + target) and a run console (outcome, 2-stage
// timeline, run-history stats). FE-only — every fact rendered here comes from data the
// page already fetched pre-redesign; see spec.md's "Data available to the page today"
// table. Route stays gated PIPELINE_READ; actions re-apply their own per-action gate below
// (NFR-Security).
const PipelineDetail = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const goBack = useGoBack();
	const location = useLocation();

	// pipelines-list-sort-and-narrow review LOW-3 — the PARENT link, carrying the list's narrowing.
	//
	// Wave 1 moved the list's search/facets/sort into its URL, which made the back ARROW restore them
	// for free (it is navigate(-1), so the query string comes back with the entry). The breadcrumb
	// LINK sitting two elements away pointed at a bare `/pipelines` and dropped them — two adjacent
	// ways back that behaved differently.
	//
	// The list hands its query string forward on the row click; this rebuilds the address from it.
	// `location.state` is per-navigation, so this is a HAND-OFF, not a second store of the narrowing —
	// the list's URL is still the only thing that decides what the list shows (spec D3 stands). On a
	// deep link / bookmark / new tab there is no state and this is the bare list path, exactly as
	// before: nothing to restore, nothing lost.
	const pipelineListUrl = buildPipelineListUrl(ROUTE_STRING.PIPELINE.LIST, location.state?.listSearch);

	const canUpdate = useAllowedAccess([AuthorizePermission.PIPELINE_UPDATE]);
	const canDelete = useAllowedAccess([AuthorizePermission.PIPELINE_DELETE]);

	// GET /api/pipelines/{id} -> PipelineDto | 404.
	const { data: pipelineData, isLoading: isLoadingPipeline, isError: isPipelineError, error: pipelineError } = useGetDetailPipeline(id);
	const pipeline = pipelineData?.data;
	const is404 = pipelineError?.response?.status === 404;

	// Read-only build coordinates (branch / dockerfile / repo / latest tag) the NEXT run
	// will use — resolved server-side under PIPELINE_READ (no REPOSITORY_READ needed).
	// `Resolved: false` => this ImageName has never been built yet (AC-8).
	// `isError` is threaded through deliberately: a FAILED build-source request is not the same fact as
	// `Resolved: false`. Conflating them made the panel assert "Not built yet — this image has no
	// successful build" about a pipeline that may have dozens of builds (adversarial-review finding).
	const { data: buildSourceData, isLoading: isLoadingBuildSource, isError: isBuildSourceError } = useGetPipelineBuildSource(id);
	const buildSource = buildSourceData?.data;

	// AC-10 — locate this pipeline inside the already-loaded grouped list
	// (GET /api/pipelines) rather than fanning out across every App Instance.
	const { isLoading: isLoadingGroupedList, isError: isGroupedListError } = useGetListPipelines();
	const { list: groupedList } = useSelector(s => s.pipeline);
	const groups = groupedList ?? [];
	const appInstanceId = useMemo(() => resolvePipelineAppInstanceId(groups, id), [groups, id]);

	const { data: appInstanceData, isError: isAppInstanceError } = useGetDetailAppInstance(appInstanceId, {
		enabled: !!appInstanceId
	});
	const appInstance = appInstanceData?.data;
	const containerNameById = useMemo(() => buildContainerNameById(appInstance?.ContainerApps), [appInstance]);

	// AC-10 — never surface the raw ContainerAppId int. While the grouped list / owning
	// instance is still loading, show a resolving state; once settled, either a resolvable
	// container name + link, or a graceful "container unavailable" fallback (orphaned
	// pipeline — absent from every group, or its container missing from the instance).
	// A FAILED lookup is a settled outcome, not an in-progress one. Previously an errored
	// GET /api/pipelines or GET /api/app-instances/{id} left `appInstance` permanently undefined, which
	// this expression read as "still resolving" — so the whole Target region, including Wave 2's drift
	// badge, sat as a skeleton forever instead of degrading to the AC-10 "Container unavailable" fallback
	// (adversarial-review finding). Excluding the error states lets `isContainerUnavailable` below take
	// over, which is the honest end state: we tried, we could not resolve it.
	const isContainerResolving =
		!!pipeline && !isGroupedListError && !isAppInstanceError && (isLoadingGroupedList || (!!appInstanceId && !appInstance));
	const isContainerUnavailable = !!pipeline && !isContainerResolving && (!appInstanceId || !containerNameById.has(pipeline.ContainerAppId));
	const containerName = pipeline ? containerNameById.get(pipeline.ContainerAppId) : undefined;

	// AC-9/AC-11 — resolve the latest run's Id (API contract: newest-first), then feed it
	// into exactly ONE usePollPipelineRun instance (inside PipelineRunStatusView). No
	// second poller is stood up on this page (AC-17).
	const { data: runsData, isLoading: isLoadingRuns, isError: isRunsError, isFetching: isFetchingRuns } = useGetListPipelineRuns(id);
	// Memoised so the `?? []` fallback doesn't mint a new array identity on every render —
	// without this, `React.memo` on RunConsole/RunHistoryStats never hits and the stats are
	// recomputed on every tick of the elapsed-time ticker below.
	const runs = useMemo(() => runsData?.data ?? [], [runsData]);
	const latestRun = runs[0];
	const latestRunId = latestRun?.Id;

	// Threaded up from PipelineRunStatusView's own (single) poll via the additive
	// `onRunChange` callback (AC-17), so the outcome header + stage timeline can be pinned
	// and the Run action can be disabled while non-terminal (AC-14) WITHOUT a second poller.
	const [liveRun, setLiveRun] = useState(null);
	useEffect(() => {
		setLiveRun(null);
	}, [latestRunId]);
	const displayRun = liveRun ?? latestRun;

	// Target drift, the build-source tag caption and the run-history stats are all resolved server-side and
	// fetched ONCE on mount. Nothing re-read them when a run finished, so straight after a successful deploy
	// the page went on asserting the PRE-run drift state on the same screen that was live-rendering the run
	// which invalidated it (adversarial-review finding). Invalidate on the observed terminal transition —
	// the moment the facts actually change. Keyed on Id as well as Status so a new run's own terminal
	// transition re-fires it; this issues no poll of its own, so Wave 1's single-poller invariant holds.
	const queryClient = useQueryClient();
	useEffect(() => {
		if (!displayRun || !isTerminalPipelineRunStatus(displayRun.Status)) {
			return;
		}
		queryClient.invalidateQueries({ queryKey: queryKeys.getDetail(id) });
		queryClient.invalidateQueries({ queryKey: queryKeys.getBuildSource(id) });
		queryClient.invalidateQueries({ queryKey: queryKeys.getRuns(id) });
	}, [displayRun?.Id, displayRun?.Status, id, queryClient]);

	const isRunNonTerminal = !!displayRun && !isTerminalPipelineRunStatus(displayRun.Status);
	// AC-14 — also hold Run disabled while the runs list is (re)fetching, e.g. the window
	// between a successful trigger (dialog closes) and getRuns(id) invalidation resolving
	// the new run into displayRun — otherwise the button briefly re-enables against a stale run.
	const isRunActionDisabled = isRunNonTerminal || isFetchingRuns;

	// AC-12/AC-17 — a single, zero-network elapsed-time ticker for the currently active
	// run, torn down the instant it's no longer needed (terminal status, a different run,
	// or unmount). Never a second poller: this issues no request, it only forces a
	// re-render every second so `Date.now() - StartedAt` recomputes.
	const [tickCount, tick] = useReducer(x => x + 1, 0);
	useEffect(() => {
		if (!displayRun || isTerminalPipelineRunStatus(displayRun.Status)) {
			return undefined;
		}
		const intervalId = setInterval(tick, 1000);
		return () => clearInterval(intervalId);
	}, [displayRun?.Id, displayRun?.Status]);

	// `tickCount` MUST be in the dep list: the interval above only forces a re-render, it does
	// not change the run's identity/status/StartedAt, so memoising on those alone would return
	// the first computed label forever and the "elapsed" readout would sit frozen for the whole
	// run. It is the tick — not the run — that makes this value stale.
	const elapsedLabel = useMemo(() => {
		if (!displayRun?.StartedAt || isTerminalPipelineRunStatus(displayRun.Status)) {
			return null;
		}
		const startedMs = new Date(displayRun.StartedAt).getTime();
		if (Number.isNaN(startedMs)) {
			return null;
		}
		return formatDurationMs(Math.max(0, Date.now() - startedMs));
	}, [tickCount, displayRun?.Status, displayRun?.StartedAt]);

	// AC-1/AC-12 — the header's duration meta: a completed duration once terminal, else the
	// live elapsed-so-far ticker above — never a fabricated/negative/NaN value.
	const durationLabel = displayRun
		? isTerminalPipelineRunStatus(displayRun.Status)
			? formatRunDuration(displayRun.StartedAt, displayRun.CompletedAt)
			: elapsedLabel
				? `${elapsedLabel} elapsed`
				: null
		: null;

	const editModal = useModal();
	const deleteModal = useModal();
	const deleteMutate = useDeletePipeline();

	const handleDelete = () => {
		deleteMutate.mutate(id, {
			onSuccess: () => {
				deleteModal.onClose();
				// Deliberately the BARE list, not `pipelineListUrl`: the row the narrowing was framed
				// around no longer exists, and restoring a filter that may now match nothing would land
				// the reader on an empty list right after a successful delete. A clean list is the
				// honest destination here. (Also why useGoBack must not be used post-delete — see its
				// doc comment: the previous entry can be the record that is gone.)
				navigate(ROUTE_STRING.PIPELINE.LIST);
			}
		});
	};

	// Deleted / non-existent pipeline: a distinct "no longer available" state (404) vs a
	// generic retry-able load error, never a crash or an indefinite spinner.
	if (isPipelineError) {
		return (
			<AppsContainer
				fullView
				title={
					<Breadcrumbs aria-label="breadcrumb">
						<Typography color="text.primary">Deployment</Typography>
						<Link component={RouterLink} to={pipelineListUrl} color="inherit" underline="hover">
							Pipelines
						</Link>
					</Breadcrumbs>
				}
			>
				<Box p={4}>
					<Alert
						severity={is404 ? 'warning' : 'error'}
						action={
							<Button color="inherit" size="small" onClick={() => navigate(pipelineListUrl)}>
								Back to Pipelines
							</Button>
						}
					>
						{is404 ? 'This pipeline is no longer available — it may have been deleted.' : 'Could not load this pipeline. Please try again.'}
					</Alert>
				</Box>
			</AppsContainer>
		);
	}

	return (
		<AppsContainer
			fullView
			title={
				<Breadcrumbs aria-label="breadcrumb">
					<Typography color="text.primary">Deployment</Typography>
					<Link component={RouterLink} to={pipelineListUrl} color="inherit" underline="hover">
						Pipelines
					</Link>
					<Typography color="text.primary">{pipeline?.Name ?? '…'}</Typography>
				</Breadcrumbs>
			}
		>
			{/* Reuse the SAME Edit dialog the (now browse-only) list used to host. */}
			<PipelineEditDialog visible={editModal.visible} pipeline={pipeline} appInstanceId={appInstanceId} onClose={editModal.onClose} />

			{/* Reuse the SAME Delete confirmation the list used to host. */}
			<DeleteDialog visible={deleteModal.visible} loading={deleteMutate.isPending} name={pipeline?.Name} handleClose={deleteModal.onClose} handleSave={handleDelete} />

			<AppsHeader>
				<Box display="flex" alignItems="center" justifyContent="space-between" gap={4} sx={{ width: '100%' }}>
					<Box display="flex" alignItems="center" gap={4} minWidth={0}>
						{/* Generic "Back", not "Back to pipelines": the pipelines list is now only the
						    fallback for a deep-linked pipeline. The label has to follow the behaviour
						    or it lies to a screen-reader user about where the button leads. */}
						<IconButton aria-label="Back" size="small" onClick={() => goBack(pipelineListUrl)}>
							<ArrowBackIcon sx={{ color: 'text.secondary' }} />
						</IconButton>
						<Box minWidth={0}>
							<Typography variant="h4" noWrap>
								{isLoadingPipeline ? <Skeleton width={200} /> : pipeline?.Name}
							</Typography>
							{/* OQ-1a — outcome promoted into the existing AppsHeader: status Chip + a
							    text.secondary meta line (duration · timestamp) beside the name. The
							    aria-live region here is the ONE status-transition announcement point on
							    the page (NFR-Accessibility) — status is never colour-alone: the Chip
							    always pairs an icon + text label. */}
							{displayRun && (
								<Box display="flex" alignItems="center" gap={1.5} mt={0.5} flexWrap="wrap">
									{/* The live region is scoped to the STATUS CHIP ALONE. The meta line beside it
									    carries the elapsed-time ticker, which re-renders every second — inside the
									    live region that turned the page's single status-transition announcement
									    into a once-per-second screen-reader flood (adversarial-review finding).
									    A status transition is worth interrupting a screen-reader user for; "1m 04s
									    elapsed", once a second, is not. The meta text stays visible to everyone and
									    is still reachable on demand; it is just no longer announced. */}
									<Box role="status" aria-live="polite" display="flex" alignItems="center">
										<PipelineRunStatusChip status={displayRun.Status} />
									</Box>
									<Typography variant="body2" color="text.secondary">
										{/* deploy-pipelines-run-overrides (Wave 4, AC-10) — this run's OWN branch, read
										    through DockerImageMetaData at the time it built (never re-resolved from the
										    pipeline's/image group's CURRENT state), so an override run stays
										    distinguishable from a default run after the fact. Rendered outside the
										    aria-live region above for the same reason the timestamp is (OQ-1a comment). */}
										{[
											durationLabel,
											displayRun.StartedAt && new Date(displayRun.StartedAt).toLocaleString(),
											`Branch: ${resolveRunBranchDisplay(displayRun.BranchName)}`
										]
											.filter(Boolean)
											.join(' · ')}
									</Typography>
								</Box>
							)}
						</Box>
					</Box>

					<Box display="flex" alignItems="center" gap={2}>
						{/* AC-13/AC-14/AC-15/AC-15a — Run stays the single highest-emphasis action
						    (variant="contained", theme primary), one click, still collects the PAT. */}
						<PipelineRunAction
							pipelineId={id}
							disabled={isRunActionDisabled}
							effectiveBranch={buildSource?.EffectiveBranchName}
							runId={displayRun?.Id}
							runStatus={displayRun?.Status}
						/>

						{/* AC-15 — Edit stays secondary. */}
						{canUpdate && (
							<Button variant="outlined" onClick={editModal.onShow} disabled={!pipeline}>
								Edit
							</Button>
						)}

						{/* AC-15/OQ-1d — Delete is demoted to an icon-only affordance, separated from
						    the Run/Edit pair by a divider, so it is no longer one mis-click from the
						    daily action. Never placed inside the source-config panel. */}
						{canDelete && (
							<>
								<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
								<Tooltip title="Delete pipeline">
									<span>
										<IconButton aria-label="Delete pipeline" color="error" onClick={deleteModal.onShow} disabled={!pipeline}>
											<DeleteIcon />
										</IconButton>
									</span>
								</Tooltip>
							</>
						)}
					</Box>
				</Box>
			</AppsHeader>

			{/* AC-17 — the ONE poller on this page. Normally renders nothing (`hideDetails`):
			    the header/timeline below render the polled `run` threaded up via `onRunChange`.
			    It sits INSIDE the content region so the two signals it does still surface — a
			    vanished run, a failing poll — land in the page's padded flow rather than above
			    the header. */}
			{latestRunId && <PipelineRunStatusView pipelineId={id} runId={latestRunId} onRunChange={setLiveRun} hideDetails />}

			{/* AC-1/AC-3 — the failure reason, above the fold: plain text, line breaks
			    preserved, never HTML, length-managed so it cannot push the timeline/config/
			    actions off screen. OQ-1a — a theme-aware Paper sub-header (background.default +
			    divider), never the mock's hardcoded navy/near-black surfaces. */}
			{displayRun?.Status === PipelineRunStatus.Failed && displayRun?.ErrorMessage && (
				<Paper
					variant="outlined"
					square
					sx={{ mx: 4, mt: 3, p: 2.5, bgcolor: 'background.default', borderColor: 'error.main', borderLeftWidth: 4 }}
				>
					<Box display="flex" alignItems="flex-start" gap={1.5}>
						<ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main', mt: 0.25 }} aria-hidden="true" />
						<Box flex={1} minWidth={0}>
							<Typography variant="subtitle2" color="error.main">
								Run failed
							</Typography>
							<RunErrorText message={displayRun.ErrorMessage} maxHeight={160} />
						</Box>
					</Box>
				</Paper>
			)}

			{/* deploy-pipelines-run-cancel (Wave 5, AC-25/OQ-D) — see CancelledRunNotice for why a
			    cancelled run’s reused `ErrorMessage` must NOT get the Failed banner’s treatment.
			    Extracted to its own component so it is actually testable (code review, M3). */}
			<CancelledRunNotice run={displayRun} />

			{isLoadingPipeline ? (
				<Box p={4}>
					<Skeleton variant="rounded" height={320} />
				</Box>
			) : (
				<Box
					sx={{
						p: 4,
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 400px) 1fr' },
						gap: 5
					}}
				>
					{/* DOM/tab order at `md`+ deliberately MATCHES the visual left-to-right order:
					    the config panel is authored first and paints LEFT (OQ-1b), the run console
					    second and paints RIGHT, so a keyboard user traverses the columns in the same
					    order a sighted user reads them (WCAG 2.4.3). Owner decision 2026-07-30.

					    Trade-off, recorded rather than hidden: below `md` the console is ordered to
					    stack FIRST visually (AC-5), but DOM order stays config-first, so screen-reader
					    reading order there is config-then-console. CSS `order` cannot make tab order
					    match visual order in BOTH layouts when the two layouts want opposite orders —
					    one of them has to diverge. Desktop wins because 1280/1440/1920 is the entire
					    verified matrix (OQ-2) and below `md` is explicitly graceful degradation, not a
					    supported target. AC-5's "reading order" clause needs amending to say so. */}
					{/* deploy-pipelines-run-provenance (Wave 2) — the three drift props below ride the
					    PipelineDto this page ALREADY fetched via useGetDetailPipeline; drift is derived
					    server-side, so no new hook, request or poller is introduced here. */}
					<Box sx={{ order: { xs: 2, md: 1 } }}>
						<SourceConfigPanel
							imageName={pipeline?.ImageName}
							buildSource={buildSource}
							isLoadingBuildSource={isLoadingBuildSource}
							isBuildSourceError={isBuildSourceError}
							containerName={containerName}
							appInstanceName={appInstance?.Name}
							appInstanceId={appInstanceId}
							containerAppId={pipeline?.ContainerAppId}
							isContainerResolving={isContainerResolving}
							isContainerUnavailable={isContainerUnavailable}
							targetDriftStatus={pipeline?.TargetDriftStatus}
							deployedTagVersion={pipeline?.DeployedTagVersion}
							latestTagVersion={pipeline?.LatestTagVersion}
						/>
					</Box>

					<Box sx={{ order: { xs: 1, md: 2 } }}>
						<RunConsole
							pipelineId={id}
							hasAnyRun={!!latestRunId}
							isLoadingRuns={isLoadingRuns}
							isRunsError={isRunsError}
							run={displayRun}
							elapsedLabel={elapsedLabel}
							runs={runs}
						/>
					</Box>
				</Box>
			)}
		</AppsContainer>
	);
};

export default PipelineDetail;
